import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Upload, CheckCircle, Clock, XCircle, RefreshCw,
  ShieldCheck, FileText, Badge, AlertCircle, FileCheck, CreditCard, X, File
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/api';
import { mediaUrl } from '../../../utils/media';

const BADGE_TYPES = [
  { id: 'TWIC', name: 'TWIC Card', description: 'Transportation Worker Identification Credential', icon: Badge },
  { id: 'TSA', name: 'TSA PreCheck', description: 'TSA Security Clearance', icon: ShieldCheck },
  { id: 'Hazmat', name: 'Hazmat Certification', description: 'Hazardous Materials Endorsement', icon: AlertCircle },
  { id: 'DOT_Physical', name: 'DOT Physical/Medical Card', description: 'Department of Transportation Medical Certification', icon: FileCheck },
  { id: 'CDL', name: 'CDL License', description: 'Commercial Driver License', icon: CreditCard },
  { id: 'Insurance', name: 'Insurance Certificate', description: 'Proof of Insurance Coverage', icon: FileText },
  { id: 'W9', name: 'W-9 Form', description: 'Tax Identification Form', icon: FileText },
  { id: 'BOL', name: 'Bill of Lading', description: 'Shipping and Freight Document', icon: FileText },
  { id: 'Other', name: 'Other Document', description: 'Any other compliance or certification document', icon: File }
];

const STATUS_CONFIG = {
  verified: { color: 'bg-green-50 border-green-200 text-green-800', icon: CheckCircle, label: 'Verified' },
  pending: { color: 'bg-yellow-50 border-yellow-200 text-yellow-800', icon: Clock, label: 'Pending Review' },
  expired: { color: 'bg-red-50 border-red-200 text-red-800', icon: XCircle, label: 'Needs Update' },
  rejected: { color: 'bg-red-50 border-red-200 text-red-800', icon: XCircle, label: 'Rejected' }
};

const CredentialManager = ({ badges, onBadgesChange }) => {
  const [newBadge, setNewBadge] = useState({ badge_type: 'TWIC' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [verificationInProgress, setVerificationInProgress] = useState({});
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const validateFile = (file) => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, and PDF files are allowed');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return false;
    }
    return true;
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && validateFile(file)) setSelectedFile(file);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) setSelectedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadBadge = async (e) => {
    e.preventDefault();
    if (!selectedFile) { toast.error('Please select a file to upload'); return; }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('doc_type', newBadge.badge_type);

      await api.post(`/documents/upload?doc_type=${newBadge.badge_type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(pct);
        },
      });

      setUploadProgress(100);
      toast.success(`${BADGE_TYPES.find(b => b.id === newBadge.badge_type)?.name || newBadge.badge_type} uploaded successfully`);
      setSelectedFile(null);
      setUploadProgress(0);
      if (onBadgesChange) onBadgesChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleVerifyBadge = async (badgeId) => {
    setVerificationInProgress(prev => ({ ...prev, [badgeId]: true }));
    try {
      await api.post(`/badges/verify/${badgeId}`);
      toast.success('Verification initiated');
      if (onBadgesChange) onBadgesChange();
    } catch {
      toast.error('Failed to verify credential');
    } finally {
      setVerificationInProgress(prev => ({ ...prev, [badgeId]: false }));
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">

      <h2 className="font-heading text-xl font-bold text-slate-900 mb-1 flex items-center gap-2">
        <Upload className="h-5 w-5 text-blue-500" />
        Certification Upload Center
      </h2>
      <p className="text-slate-600 text-sm mb-6">
        Upload your credentials to get verified. We support TWIC, TSA, Hazmat, DOT Physical, and more.
      </p>

      {/* Existing Credentials */}
      <div className="space-y-3 mb-6">
        {badges.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No credentials uploaded yet</p>
            <p className="text-sm text-slate-400">Upload your first credential below</p>
          </div>
        ) : (
          badges.map((badge) => {
            const statusCfg = STATUS_CONFIG[badge.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusCfg.icon;
            const badgeInfo = BADGE_TYPES.find(b => b.id === badge.badge_type);
            const BadgeIcon = badgeInfo?.icon || FileText;
            return (
              <div key={badge.id || badge.badge_type}
                className={`flex items-center justify-between p-4 border rounded-lg ${statusCfg.color}`}
                data-testid={`credential-${badge.badge_type}`}>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <BadgeIcon className="h-6 w-6 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{badgeInfo?.name || badge.badge_type}</p>
                    <p className="text-xs text-slate-500">{badgeInfo?.description}</p>
                    {badge.document_url && (
                      <a href={mediaUrl(badge.document_url)} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline mt-0.5 inline-block">View document</a>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${statusCfg.color}`}>
                    <StatusIcon className="h-3.5 w-3.5" />{statusCfg.label}
                  </span>
                  {badge.status === 'pending' && (
                    <button onClick={() => handleVerifyBadge(badge.id)} disabled={verificationInProgress[badge.id]}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-500 text-white rounded-md text-xs font-medium hover:bg-blue-600 disabled:opacity-50"
                      data-testid={`verify-btn-${badge.badge_type}`}>
                      {verificationInProgress[badge.id] ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                      Verify
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Upload Form */}
      <form onSubmit={handleUploadBadge} className="p-5 bg-slate-50 rounded-lg border border-slate-200">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2 text-sm">
          <Upload className="h-4 w-4" />
          Upload TWIC / Hazmat / Compliance Document and more
        </h3>

        {/* Document Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">Document Type</label>
          <select value={newBadge.badge_type} onChange={(e) => setNewBadge({ ...newBadge, badge_type: e.target.value })}
            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
            data-testid="credential-type-select">
            {BADGE_TYPES.map(badge => (
              <option key={badge.id} value={badge.id}>{badge.name} — {badge.description}</option>
            ))}
          </select>
        </div>

        {/* Drag & Drop Zone */}
        <div
          onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
          onClick={() => !selectedFile && fileInputRef.current?.click()}
          className={`relative mb-4 rounded-lg border-2 border-dashed transition-all cursor-pointer
            ${dragActive ? 'border-blue-500 bg-blue-50' : selectedFile ? 'border-green-300 bg-green-50' : 'border-slate-300 bg-white hover:border-blue-400 hover:bg-blue-50/50'}`}
          data-testid="upload-dropzone"
        >
          <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleFileSelect}
            className="hidden" data-testid="document-file-input" />

          {selectedFile ? (
            <div className="flex items-center gap-3 p-4">
              <div className="p-2.5 bg-green-100 rounded-lg"><FileCheck className="h-6 w-6 text-green-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{selectedFile.name}</p>
                <p className="text-xs text-slate-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB · {selectedFile.type.split('/')[1]?.toUpperCase()}</p>
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setUploadProgress(0); }}
                className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" data-testid="remove-selected-file">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 px-4">
              <div className={`p-3 rounded-full mb-3 ${dragActive ? 'bg-blue-100' : 'bg-slate-100'}`}>
                <Upload className={`h-6 w-6 ${dragActive ? 'text-blue-600' : 'text-slate-400'}`} />
              </div>
              <p className="text-sm font-medium text-slate-700">
                {dragActive ? 'Drop your file here' : 'Drag & drop your document here'}
              </p>
              <p className="text-xs text-slate-400 mt-1">or click to browse — JPG, PNG, PDF up to 10MB</p>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {uploading && (
          <div className="mb-4" data-testid="upload-progress">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-slate-600">Uploading...</span>
              <span className="text-xs font-bold text-blue-600">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
              <motion.div
                className="bg-blue-600 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${uploadProgress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        )}

        <button type="submit" disabled={uploading || !selectedFile}
          className="w-full bg-slate-900 text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-slate-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="upload-credential-btn">
          {uploading ? (
            <><RefreshCw className="h-4 w-4 animate-spin" />Uploading {uploadProgress}%</>
          ) : (
            <><Upload className="h-4 w-4" />Upload Document</>
          )}
        </button>
      </form>
    </motion.div>
  );
};

export default CredentialManager;
